import { useHotkeys as originalUseHotkeys } from "react-hotkeys-hook";
import { useEffect, useMemo, useCallback } from "react";
import useHotkeysContext from "hooks/useHotkeysContext";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
export const DEFAULT_SPLIT_KEY = ",";
export const DEFAULT_COMBINATION_KEY = "+";
const defaultOptions = {
  preventDefault: true,
  enabled: true,
  enableOnContentEditable: false,
  combinationKey: DEFAULT_COMBINATION_KEY,
  splitKey: DEFAULT_SPLIT_KEY,
  keyup: void 0,
  keydown: true,
  description: void 0,
  document: void 0,
  ignoreModifiers: false,
  displayInOverview: void 0
};
const catchErrors = (hotKeysCollections, actionKey, scope) => {
  if (!hotKeysCollections[scope]) {
    throw Error(`Scope "${scope}" does not exist in hotkeys collection.`);
  }
  if (!hotKeysCollections[scope].actions[actionKey]) {
    throw Error(`Action "${actionKey}" does not exist in hotkeys collection of "${scope}" scope.`);
  }
};
const useHotkey = ({
  actionKey,
  callback = void 0,
  scope,
  options = void 0,
  dependencies = void 0
}) => {
  var _a, _b, _c;
  const sendTelemetry = useSendTelemetry();
  const { hotKeysCollections, addActiveHotkey, removeActiveHotkey } = useHotkeysContext();
  catchErrors(hotKeysCollections, actionKey, scope);
  const mergedOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      scopes: scope
    }),
    [options, scope]
  );
  const callbackWithTelemetry = useCallback(
    (event, handler) => {
      var _a2, _b2, _c2;
      sendTelemetry(TELEMETRY_EVENT_TYPE.SHORTCUT_TYPED, {
        event_details: { actionKey, scope, keys: (_c2 = (_b2 = (_a2 = hotKeysCollections == null ? void 0 : hotKeysCollections[scope]) == null ? void 0 : _a2.actions) == null ? void 0 : _b2[actionKey]) == null ? void 0 : _c2.keys }
      });
      callback(event, handler);
    },
    [actionKey, callback, hotKeysCollections, scope, sendTelemetry]
  );
  useEffect(() => {
    addActiveHotkey({
      scope,
      actionKey,
      options: {
        scope,
        enabled: mergedOptions.enabled,
        displayInOverview: mergedOptions.displayInOverview,
        combinationKey: mergedOptions.combinationKey,
        splitKey: mergedOptions.splitKey
      }
    });
    return () => removeActiveHotkey({ scope, actionKey });
  }, [
    actionKey,
    addActiveHotkey,
    scope,
    removeActiveHotkey,
    mergedOptions.combinationKey,
    mergedOptions.enabled,
    mergedOptions.displayInOverview,
    mergedOptions.splitKey
  ]);
  return originalUseHotkeys(
    (_c = (_b = (_a = hotKeysCollections == null ? void 0 : hotKeysCollections[scope]) == null ? void 0 : _a.actions) == null ? void 0 : _b[actionKey]) == null ? void 0 : _c.keys,
    callbackWithTelemetry,
    mergedOptions,
    dependencies
  );
};
export default useHotkey;
